/// <reference path="../../types/index.d.ts" />

/**
 * Game controller - manages game state, pause/resume, and input handling
 *
 * Uses async/await with SimplePromise for input resolution, and supports
 * queuing live timers that fire while the game is awaiting user input.
 */

import { Globals } from './Globals';

declare const TheGame: any;

// Forward declarations - will be imported from other modules
declare function UpdateStatusBars(): void;
declare namespace GameCommands {
    function runCommandsAsync(commands: any[]): Promise<boolean>;
}
declare namespace GameTimers {
    function runTimerEvents(): void;
    function runTimerAsync(timer: any, checkActive: boolean): Promise<void>;
    function runTimerEventsAsync(): Promise<void>;
}
declare namespace GameUI {
    function disableSaveAndLoad(): void;
    function enableSaveAndLoad(): void;
    function hideGameElements(): void;
    function showGameElements(): void;
    function onInteractionResume(noLine?: boolean): void;
    function disableMenuButtons(): void;
    function enableMenuButtons(): void;
    function showPauseButton(): void;
    function hidePauseButton(): void;
}

export class SimplePromise {
    private inner: Promise<void>;
    resolve!: () => void;
    reject!: (reason?: any) => void;

    constructor() {
        this.inner = new Promise<void>((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }

    then<TResult1 = void, TResult2 = never>(
        onfulfilled?: ((value: void) => TResult1 | PromiseLike<TResult1>) | undefined | null,
        onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
    ): Promise<TResult1 | TResult2> {
        return this.inner.then(onfulfilled as any, onrejected as any);
    }

    catch<TResult = never>(onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null): Promise<void | TResult> {
        return this.inner.catch(onrejected as any);
    }

    finally(onfinally?: (() => void) | undefined | null): Promise<void> {
        return this.inner.finally(onfinally as any);
    }

    [Symbol.toStringTag] = 'SimplePromise';
}

export class GameController {
    gamePaused: boolean = false;
    gameAwaitingInput: boolean = false;
    inputPromises: SimplePromise[] = [];
    liveTimersToRun: any[] = [];
    private pendingTimerCheck: boolean = false;

    title(): string {
        if (typeof TheGame !== 'undefined' && TheGame && TheGame.Title) {
            return TheGame.Title;
        }

        const urlParts = window.location.href.split('/');
        const penultimateUrlPart = urlParts[urlParts.length - 2];
        return decodeURIComponent(penultimateUrlPart);
    }

    async startAwaitingInputAsync(noLine?: boolean): Promise<void> {
        this.gameAwaitingInput = true;
        (window as any).Globals.runningLiveTimerCommands = false;

        GameUI.disableSaveAndLoad();
        GameUI.hideGameElements();
        GameUI.disableMenuButtons();
        GameUI.onInteractionResume(noLine);

        await this.userActionAsync();
    }

    async stopAwaitingInputAsync(resume: boolean = true): Promise<void> {
        this.gameAwaitingInput = false;
        GameUI.enableSaveAndLoad();
        GameUI.showGameElements();
        GameUI.enableMenuButtons();

        const liveTimersToRun = this.liveTimersToRun;
        this.liveTimersToRun = [];
        for (const timer of liveTimersToRun) {
            await GameTimers.runTimerAsync(timer, true);
        }

        if (resume) {
            this.resumeExecution();
        }
    }

    queueLiveTimer(timer: any): void {
        if (!this.gameAwaitingInput) {
            console.warn('queueLiveTimer() should only be called while waiting for input');
        }
        this.liveTimersToRun.push(timer);
    }

    async executeAndRunTimersAsync(fn: () => void | Promise<void>): Promise<void> {
        const wasRunningTimers = (window as any).Globals.bRunningTimers;
        await fn();
        if (!wasRunningTimers) {
            await GameTimers.runTimerEventsAsync();
            UpdateStatusBars();
        }
    }

    pauseAsync(noWait: boolean = false): Promise<void> {
        this.gamePaused = true;
        GameUI.disableSaveAndLoad();
        GameUI.hideGameElements();
        GameUI.showPauseButton();
        GameUI.onInteractionResume();

        if (noWait) return Promise.resolve();
        return this.userActionAsync();
    }

    pause(noWait: boolean = false): void {
        void this.pauseAsync(noWait);
    }

    awaitContinue(): Promise<void> {
        return this.pauseAsync();
    }

    continue(noResume: boolean = false): boolean | Promise<void> {
        this.gamePaused = false;
        GameUI.enableSaveAndLoad();
        GameUI.showGameElements();
        GameUI.hidePauseButton();

        return noResume ? false : this.resumeExecution();
    }

    shouldRunCommands(): boolean {
        return !(this.gamePaused || this.gameAwaitingInput);
    }

    userActionAsync(): Promise<void> {
        const inputPromise = new SimplePromise();
        this.inputPromises.push(inputPromise);
        return inputPromise as any;
    }

    resumeExecution(): boolean | Promise<void> {
        const inputPromise = this.inputPromises.pop();
        if (inputPromise !== undefined) {
            inputPromise.resolve();
            return true;
        }
        return false;
    }

    notifyAdvancedScriptsDrained(): void {
        if ((Globals as any).advancedScriptDepth) return;
        this.pendingTimerCheck = false;
        if (this.shouldRunCommands() && !(Globals as any).bRunningTimers && !(Globals as any).advancedScriptDepth) {
            GameTimers.runTimerEvents();
            UpdateStatusBars();
        }
    }

    reset(): void {
        this.gamePaused = false;
        this.gameAwaitingInput = false;
        this.inputPromises = [];
        this.liveTimersToRun = [];
        GameUI.enableSaveAndLoad();
    }

    startAwaitingInput(): void {
        this.gameAwaitingInput = true;
        (window as any).Globals.runningLiveTimerCommands = false;
        GameUI.disableSaveAndLoad();
        GameUI.hideGameElements();
        GameUI.disableMenuButtons();
    }

    stopAwaitingInput(): void {
        this.gameAwaitingInput = false;
        GameUI.enableSaveAndLoad();
        GameUI.showGameElements();
        GameUI.enableMenuButtons();
    }

    executeAndRunTimers(fn: () => void): void {
        fn();
        if (this.shouldRunCommands()) {
            GameTimers.runTimerEvents();
            UpdateStatusBars();
        }
    }

    runAfterPause(fn: () => void): void {
        if (this.shouldRunCommands()) {
            fn();
        }
    }

    runNextAfterPause(fn: () => void): void {
        if (this.shouldRunCommands()) {
            fn();
        }
    }

    async runCommandsAndCheckTimers(): Promise<void> {
        if (this.shouldRunCommands() && !(Globals as any).bRunningTimers && !(Globals as any).advancedScriptDepth) {
            await GameTimers.runTimerEventsAsync();
            UpdateStatusBars();
        }
    }
}

export const gameController = new GameController();
